import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "checkbox",      // Individual row checkboxes
    "row",           // Table rows (for selected class)
    "headerCheckbox", // The checkbox in the header
    "counter",       // Text showing "X selected"
    "menu",          // Dropdown menu container
    "selectNoneCount",
    "selectPageCount",
    "selectAllCount"
  ]

  static values = {
    totalCount: { type: Number, default: 0 },  // Total items across all pages
    pageCount: { type: Number, default: 0 }    // Items on current page
  }

  connect() {
    this.allSelected = false  // Track if "select all" (across pages) is active
    this.updateUI()
  }

  // Called when a row checkbox changes
  toggle(event) {
    const checkbox = event.target
    const row = checkbox.closest("tr")
    
    if (checkbox.checked) {
      row.classList.add("bg-base-200")
    } else {
      row.classList.remove("bg-base-200")
      // Unchecking any box exits "select all" mode
      this.allSelected = false
    }
    
    this.updateUI()
  }

  // Select no items
  selectNone() {
    this.allSelected = false
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = false
      checkbox.closest("tr").classList.remove("bg-base-200")
    })
    this.updateUI()
    this.closeMenu()
  }

  // Select all items on current page only
  selectPage() {
    this.allSelected = false
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = true
      checkbox.closest("tr").classList.add("bg-base-200")
    })
    this.updateUI()
    this.closeMenu()
  }

  // Select all items across all pages
  selectAll() {
    this.allSelected = true
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = true
      checkbox.closest("tr").classList.add("bg-base-200")
    })
    this.updateUI()
    this.closeMenu()
  }

  // Toggle dropdown menu visibility
  toggleMenu() {
    this.menuTarget.classList.toggle("dropdown-open")
  }

  closeMenu() {
    this.menuTarget.classList.remove("dropdown-open")
  }

  // Perform a bulk action - submits selected IDs to the provided URL
  performAction(event) {
    event.preventDefault()
    
    const url = event.currentTarget.dataset.bulkSelectUrl
    const method = event.currentTarget.dataset.bulkSelectMethod || "POST"
    const useTurboModal = event.currentTarget.dataset.bulkSelectTurboModal === "true"

    const selectedIds = this.getSelectedIds()
    
    if (selectedIds.length === 0) {
      this.closeMenu()
      return
    }

    // Build URL with query parameters
    const urlWithParams = new URL(url, window.location.origin)
    selectedIds.forEach(id => urlWithParams.searchParams.append("bulk_select[object_ids][]", id))
    if (this.allSelected) {
      urlWithParams.searchParams.set("bulk_select[select_all]", "true")
    }

    // If turbo modal is requested, open it instead of submitting a form
    if (useTurboModal) {
      const turboFrame = document.getElementById("turbo-modal-frame")
      if (turboFrame && typeof turbo_modal !== "undefined") {
        turbo_modal.showModal()
        turboFrame.src = urlWithParams.toString()
      }
      this.closeMenu()
      return
    }

    // Create and submit a form with the selected IDs
    const form = document.createElement("form")
    form.method = method
    form.action = url
    
    // Add CSRF token
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    if (csrfToken) {
      const csrfInput = document.createElement("input")
      csrfInput.type = "hidden"
      csrfInput.name = "authenticity_token"
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }

    // Add method override if not POST
    if (method !== "POST") {
      const methodInput = document.createElement("input")
      methodInput.type = "hidden"
      methodInput.name = "_method"
      methodInput.value = method
      form.appendChild(methodInput)
    }

    // Add selected IDs
    selectedIds.forEach(id => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "bulk_select[object_ids][]"
      input.value = id
      form.appendChild(input)
    })

    // If "select all" is active, tell the server to operate on all records
    if (this.allSelected) {
      const selectAllInput = document.createElement("input")
      selectAllInput.type = "hidden"
      selectAllInput.name = "bulk_select[select_all]"
      selectAllInput.value = "true"
      form.appendChild(selectAllInput)
    }

    document.body.appendChild(form)
    form.submit()
    
    this.closeMenu()
  }

  // Get array of selected checkbox values
  getSelectedIds() {
    return this.checkboxTargets
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.value)
  }

  // Get count of selected items
  get selectedCount() {
    return this.checkboxTargets.filter(checkbox => checkbox.checked).length
  }

  // Update all UI elements based on current selection state
  updateUI() {
    const selected = this.selectedCount
    const pageTotal = this.checkboxTargets.length
    const allTotal = this.totalCountValue || pageTotal

    // Update header checkbox state
    if (this.hasHeaderCheckboxTarget) {
      if (selected === 0) {
        this.headerCheckboxTarget.checked = false
        this.headerCheckboxTarget.indeterminate = false
      } else if (selected === pageTotal) {
        this.headerCheckboxTarget.checked = true
        this.headerCheckboxTarget.indeterminate = false
      } else {
        this.headerCheckboxTarget.checked = false
        this.headerCheckboxTarget.indeterminate = true
      }
    }

    // Update counter text
    if (this.hasCounterTarget) {
      if (selected === 0) {
        this.counterTarget.textContent = ""
        this.counterTarget.classList.add("hidden")
      } else {
        // Show total count if "select all" is active, otherwise show page selection count
        const displayCount = this.allSelected ? allTotal : selected
        this.counterTarget.textContent = `${displayCount} selected`
        this.counterTarget.classList.remove("hidden")
      }
    }

    // Update dropdown option counts
    if (this.hasSelectNoneCountTarget) {
      this.selectNoneCountTarget.textContent = `(0 items)`
    }
    if (this.hasSelectPageCountTarget) {
      this.selectPageCountTarget.textContent = `(${pageTotal} items)`
    }
    if (this.hasSelectAllCountTarget) {
      this.selectAllCountTarget.textContent = `(${allTotal} items)`
    }
  }

  // Close dropdown when clicking outside
  closeOnOutsideClick = (event) => {
    if (this.hasMenuTarget && !this.menuTarget.contains(event.target)) {
      this.closeMenu()
    }
  }

  // Header checkbox click toggles between select none and select page
  toggleHeaderCheckbox(event) {
    if (this.selectedCount === this.checkboxTargets.length) {
      this.selectNone()
    } else {
      this.selectPage()
    }
  }
}

